/**
 * "Remove all devices" on the person's own Account page (board onboarding-r2). Paseo can't drop one device, so this
 * re-keys their Paseo: every device paired to their account must pair again. After a confirm that says so, the new
 * pairing link shows at once, in the same panel the dashboard pairs with.
 */
import { useState } from 'react'
import { keyFingerprint, removeQuestion, rotateProblem, sharingWith } from '../../domain/devices'
import type { RotateResult } from '../../domain/schemas'
import { Card, ConfirmAction, ErrorNote, Stack, Text } from '../../ui'
import { useLoad } from '../useLoad'
import { useFounder } from './context'
import { PairPanel } from './panels'

export function RemoveDevices() {
  const f = useFounder()
  const team = useLoad(() => f.api.people(), [f.api])
  const [working, setWorking] = useState(false)
  const [done, setDone] = useState<RotateResult | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const devices = f.state.world.devices ?? f.personSetup.devices
  const others = sharingWith(team.data?.people ?? [], f.me.person.id)

  async function remove() {
    setWorking(true)
    setFailed(null)
    try {
      setDone(await f.api.rotatePairing())
      void f.reload()
      void f.reloadSetup()
    } catch (e) {
      setFailed(rotateProblem(e))
    } finally {
      setWorking(false)
    }
  }

  const fingerprint = done?.pairing ? keyFingerprint(done.pairing.url) : null
  return (
    <Card title="Devices">
      <Stack gap="sm">
        {done ? (
          <>
            <Text as="p" size="base" weight="read">
              Every device is removed. Pair the ones you keep again with this new link; the old one no longer works.
            </Text>
            {fingerprint && (
              <Text as="p" tone="muted" size="sm">
                Paseo may ask you to confirm a changed key for your computer. Accept it if it reads <Text mono size="sm">{fingerprint}</Text>.
              </Text>
            )}
            <PairPanel initial={done.pairing} />
          </>
        ) : (
          <>
            <Text as="p" size="base" weight="read">
              {devices ? (devices.seen ? `${devices.seen} device${devices.seen === 1 ? '' : 's'} paired to your computer.` : 'No device paired yet.') : 'The phones, iPads and computers you paired to your computer.'}{' '}
              Lost one, or paired one you shouldn't have? Remove them all. Paseo can't remove just one, so every device pairs again with a new link.
            </Text>
            {failed && <ErrorNote title="Your devices weren't removed." reason={failed} onRetry={() => void remove()} />}
            <ConfirmAction label="Remove all devices" strength="confirm" yes="Remove them all" variant="line" question={removeQuestion(others)} working={working} onConfirm={() => void remove()} />
          </>
        )}
      </Stack>
    </Card>
  )
}
